import { useState, useEffect } from 'react';
import { organizationService } from '../../services/organizationService';
import type { Organization, CreateOrganizationRequest, UpdateOrganizationRequest } from '../../types/organization';
import { MIN_SEARCH_LENGTH } from '../../utils/searchConstants';

interface UseOrganizationDataParams {
  companyId: number | null;
}

export function useOrganizationData({ companyId }: UseOrganizationDataParams) {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);

  // Filters
  const [filterName, setFilterName] = useState('');
  const [debouncedName, setDebouncedName] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showOnlyActive, setShowOnlyActive] = useState(true);

  // Dialogs
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [organizationToEdit, setOrganizationToEdit] = useState<Organization | null>(null);

  // Debounce filter
  useEffect(() => {
    if (filterName.length > 0 && filterName.length < MIN_SEARCH_LENGTH) return;
    const timer = setTimeout(() => {
      setDebouncedName(filterName);
      setPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [filterName]);

  // Fetch organizations
  useEffect(() => {
    const fetchData = async () => {
      if (!companyId) {
        setError('Ідентифікатор компанії відсутній');
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const response = await organizationService.search(companyId, {
          search: debouncedName || undefined,
          includeArchived: !showOnlyActive,
          page: page - 1,
          size: 20,
        });
        setOrganizations(response.content);
        setTotalPages(response.totalPages);
        setError(null);
      } catch (err) {
        console.error('Error fetching organizations:', err);
        setError('Не вдалося завантажити організації');
      } finally {
        setLoading(false);
        setIsFirstLoad(false);
      }
    };

    fetchData();
  }, [companyId, page, debouncedName, showOnlyActive]);

  const handleCreateOrganization = async (data: CreateOrganizationRequest) => {
    if (!companyId) return;
    const newOrganization = await organizationService.create(data);
    setOrganizations((prev) => [...prev, newOrganization]);
  };

  const handleDeleteOrganization = async (organizationId: number) => {
    if (!window.confirm('Ви впевнені, що хочете архівувати цю організацію?')) return;
    await organizationService.delete(organizationId);
    setOrganizations((prev) => (
      showOnlyActive
        ? prev.filter((o) => o.id !== organizationId)
        : prev.map((o) => (o.id === organizationId ? { ...o, deactivatedDate: new Date().toISOString() } : o))
    ));
  };

  const handleRestoreOrganization = async (organizationId: number) => {
    await organizationService.restore(organizationId);
    setOrganizations((prev) => prev.map((o) => (
      o.id === organizationId ? { ...o, deactivatedDate: undefined } : o
    )));
  };

  const handleEditClick = (organization: Organization) => {
    setOrganizationToEdit(organization);
    setEditDialogOpen(true);
  };

  const handleUpdateOrganization = async (organizationId: number, data: UpdateOrganizationRequest) => {
    if (!companyId) return;
    const updated = await organizationService.update(organizationId, data);
    setOrganizations((prev) => prev.map((o) => (o.id === organizationId ? updated : o)));
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const handleShowOnlyActiveChange = (value: boolean) => {
    setShowOnlyActive(value);
    setPage(1);
  };

  const handleClearFilters = () => {
    setFilterName('');
    handleShowOnlyActiveChange(true);
  };

  return {
    data: {
      organizations,
      loading,
      isFirstLoad,
      error,
    },
    pagination: {
      page,
      count: totalPages,
      onChange: handlePageChange,
    },
    filters: {
      filterName,
      setFilterName,
      debouncedName,
      showFilters,
      setShowFilters,
      showOnlyActive,
      setShowOnlyActive: handleShowOnlyActiveChange,
      handleClearFilters,
      hasActiveFilters: !!filterName || !showOnlyActive,
    },
    crud: {
      handleCreateOrganization,
      handleDeleteOrganization,
      handleRestoreOrganization,
      handleEditClick,
      handleUpdateOrganization,
    },
    dialogs: {
      createDialogOpen,
      setCreateDialogOpen,
      editDialogOpen,
      setEditDialogOpen,
      organizationToEdit,
      setOrganizationToEdit,
    },
  };
}
